import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getArticlesByCategory, type Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'puberty');

  const category = useMemo(() => getCategoryBySlug(activeSlug), [activeSlug]);
  const articles: Article[] = useMemo(
    () => (category ? getArticlesByCategory(category.id) : []),
    [category],
  );

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl text-foreground-700 mb-2">分类未找到</h2>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Category Header */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200">首页</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-foreground-700">{category.name}</span>
            </nav>

            <div className="flex items-start gap-5">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className={`${category.icon} text-2xl text-primary-600`} />
              </div>
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {category.name}
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200/60 bg-background-50 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-6 md:px-10 overflow-x-auto">
            <div className="flex items-center gap-1 py-3">
              {categories.map((tabCat) => {
                const isActive = activeSlug === tabCat.slug;
                return (
                  <Link
                    key={tabCat.slug}
                    to={`/category/${tabCat.slug}`}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/60'
                    }`}
                  >
                    {tabCat.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.01]"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2.5 flex-wrap">
                      <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                        {article.categoryName}
                      </span>
                      <span>{article.readTime}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 mb-2 leading-snug line-clamp-2 text-[1.05rem]">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}